import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Boxes, Wallet, TrendingUp, Package, AlertTriangle, Search, RefreshCw, ArrowDownUp } from 'lucide-react';
import api, { fmtIDR } from '@/lib/api';
import { toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

/** Compact IDR (Rp 1,2 jt / Rp 3,5 M / Rp 12 rb) untuk metric card. */
const fmtCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000_000) return `Rp ${(v / 1_000_000_000).toFixed(2).replace('.', ',')} M`;
  if (v >= 1_000_000)     return `Rp ${(v / 1_000_000).toFixed(2).replace('.', ',')} jt`;
  if (v >= 1_000)         return `Rp ${(v / 1_000).toFixed(1).replace('.', ',')} rb`;
  return `Rp ${v.toLocaleString('id-ID')}`;
};

const MetricCard = ({ icon: Icon, label, value, sub, tone = 'default', testid }) => {
  const tones = {
    default: 'from-teal-500/10 to-teal-500/0 border-teal-500/20 text-teal-700 dark:text-teal-300',
    warning: 'from-amber-500/10 to-amber-500/0 border-amber-500/20 text-amber-700 dark:text-amber-300',
    success: 'from-emerald-500/10 to-emerald-500/0 border-emerald-500/20 text-emerald-700 dark:text-emerald-300',
    info:    'from-sky-500/10 to-sky-500/0 border-sky-500/20 text-sky-700 dark:text-sky-300',
  };
  return (
    <Card className={`border bg-gradient-to-br ${tones[tone]}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold opacity-80">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <div className="mt-2 text-2xl font-heading font-semibold text-foreground tabular-nums" title={typeof value === 'string' ? value : ''}>
          {value}
        </div>
        {sub && <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
};

const SORT_OPTIONS = [
  { key: 'asset_value',      label: 'Nilai Aset (terbesar)' },
  { key: 'hpp_value',        label: 'Nilai HPP (terbesar)' },
  { key: 'potential_profit', label: 'Potensi Laba (terbesar)' },
  { key: 'stock',            label: 'Stok (terbanyak)' },
  { key: 'margin_pct',       label: 'Margin % (tertinggi)' },
  { key: 'name',             label: 'Nama (A-Z)' },
];

export default function OwnerProductAssets() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('asset_value');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/products/assets/summary');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal memuat aset produk');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filteredProducts = useMemo(() => {
    if (!data?.products) return [];
    const q = search.trim().toLowerCase();
    let arr = !q ? [...data.products] : data.products.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q)
    );
    if (sortKey === 'name') arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else arr.sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
    return arr;
  }, [data, search, sortKey]);

  const s = data?.summary;
  const totalAsset = s?.total_asset_value || 0;
  const totalHpp   = s?.total_hpp_value || 0;
  const totalProfit = s?.total_potential_profit || 0;
  const margin = s?.overall_margin_pct || 0;

  return (
    <AppShell nav={ownerNav} title="Aset Produk Perusahaan">
      <div className="space-y-5">
        {/* Info Header */}
        <Card>
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-muted-foreground">
              Ringkasan <b className="text-foreground">kekayaan perusahaan</b> yang tersimpan pada stok barang.
              Menghitung <b>stok × harga jual</b> (nilai aset) beserta <b>stok × HPP</b> (modal) dan potensi laba.
            </div>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span>Dihitung pada: <b className="text-foreground">{data ? formatDate(data.generated_at) : '-'}</b></span>
              <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="assets-refresh">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricCard
            icon={Boxes}
            label="Nilai Aset (Harga Jual)"
            value={fmtCompact(totalAsset)}
            sub={<span title={fmtIDR(totalAsset)}>{fmtIDR(totalAsset)}</span>}
            tone="info"
            testid="metric-asset"
          />
          <MetricCard
            icon={Wallet}
            label="Total Modal / HPP"
            value={fmtCompact(totalHpp)}
            sub={<span title={fmtIDR(totalHpp)}>{fmtIDR(totalHpp)}</span>}
            tone="default"
            testid="metric-hpp"
          />
          <MetricCard
            icon={TrendingUp}
            label="Potensi Laba"
            value={fmtCompact(totalProfit)}
            sub={<span>{margin.toFixed(1)}% margin overall</span>}
            tone="success"
            testid="metric-profit"
          />
          <MetricCard
            icon={Package}
            label="Total Unit Stok"
            value={(s?.total_units || 0).toLocaleString('id-ID')}
            sub={`${s?.total_products || 0} produk aktif`}
            tone="default"
            testid="metric-units"
          />
        </div>

        {/* Secondary indicators */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-3">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wider">HPP Terisi</div>
              <div className="text-lg font-semibold tabular-nums">{s?.products_with_hpp || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wider">HPP Belum Diisi</div>
              <div className="text-lg font-semibold tabular-nums text-amber-600">{s?.products_without_hpp || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wider">Stok Rendah</div>
              <div className="text-lg font-semibold tabular-nums text-amber-600">{s?.low_stock_count || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wider">Stok Habis</div>
              <div className="text-lg font-semibold tabular-nums text-rose-600">{s?.out_of_stock_count || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Categories breakdown */}
        {data?.categories?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Boxes className="h-4 w-4" /> Rincian per Kategori
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[280px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead>Kategori</TableHead>
                      <TableHead className="text-right">Jumlah Produk</TableHead>
                      <TableHead className="text-right">Total Stok</TableHead>
                      <TableHead className="text-right">Nilai Aset</TableHead>
                      <TableHead className="text-right">Modal (HPP)</TableHead>
                      <TableHead className="text-right">Potensi Laba</TableHead>
                      <TableHead className="text-right">% dari Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.categories.map((c) => {
                      const share = totalAsset > 0 ? (c.asset_value / totalAsset) * 100 : 0;
                      return (
                        <TableRow key={c.category} data-testid={`assets-cat-${c.category}`}>
                          <TableCell className="font-medium">{c.category}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.product_count}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.stock.toLocaleString('id-ID')}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium">{fmtIDR(c.asset_value)}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{fmtIDR(c.hpp_value)}</TableCell>
                          <TableCell className="text-right tabular-nums text-emerald-600 font-medium">{fmtIDR(c.potential_profit)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-teal-500" style={{ width: `${Math.min(100, share)}%` }} />
                              </div>
                              <span className="text-[11px] tabular-nums text-muted-foreground w-10 text-right">{share.toFixed(1)}%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Products detail */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4" /> Rincian per Produk
                <Badge variant="secondary" className="ml-2 tabular-nums" data-testid="assets-count">{filteredProducts.length}</Badge>
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Cari nama / SKU / kategori..."
                    className="pl-7 h-8 w-64"
                    data-testid="assets-search"
                  />
                </div>
                <div className="inline-flex items-center gap-1 border border-border/60 rounded-md px-2 h-8 bg-background text-xs">
                  <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
                  <select
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value)}
                    className="bg-transparent focus:outline-none text-xs"
                    data-testid="assets-sort"
                  >
                    {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Memuat data...</div>
            ) : filteredProducts.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Tidak ada produk sesuai pencarian.</div>
            ) : (
              <div className="max-h-[560px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead>Produk</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead className="text-right">Stok</TableHead>
                      <TableHead className="text-right">Harga Jual</TableHead>
                      <TableHead className="text-right">HPP / Unit</TableHead>
                      <TableHead className="text-right">Nilai Aset</TableHead>
                      <TableHead className="text-right">Modal (HPP)</TableHead>
                      <TableHead className="text-right">Potensi Laba</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.map((p) => (
                      <TableRow key={p.id} data-testid={`assets-row-${p.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {p.photo_url ? (
                              <img src={p.photo_url} alt={p.name} className="h-8 w-8 rounded object-cover border" />
                            ) : (
                              <div className="h-8 w-8 rounded bg-muted flex items-center justify-center text-muted-foreground">
                                <Package className="h-4 w-4" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-medium truncate max-w-[240px]">{p.name}</div>
                              <div className="text-[11px] text-muted-foreground font-mono">{p.sku}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{p.category}</Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <div className="flex items-center gap-1 justify-end">
                            {p.is_out_of_stock ? (
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Habis</Badge>
                            ) : p.is_low_stock ? (
                              <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">Rendah</Badge>
                            ) : null}
                            <span className="font-medium">{p.stock.toLocaleString('id-ID')}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtIDR(p.selling_price)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {p.hpp_set ? fmtIDR(p.total_hpp) : (
                            <span className="inline-flex items-center gap-1 text-amber-600 text-[11px]">
                              <AlertTriangle className="h-3 w-3" /> Belum diisi
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{fmtIDR(p.asset_value)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{fmtIDR(p.hpp_value)}</TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-600 font-medium">{fmtIDR(p.potential_profit)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          <span className={p.margin_pct >= 20 ? 'text-emerald-600' : p.margin_pct >= 10 ? 'text-amber-600' : 'text-rose-600'}>
                            {p.margin_pct.toFixed(1)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total row */}
                    <TableRow className="bg-muted/40 font-medium sticky bottom-0">
                      <TableCell colSpan={2} className="text-right">TOTAL ({filteredProducts.length} produk)</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {filteredProducts.reduce((a, p) => a + (p.stock || 0), 0).toLocaleString('id-ID')}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell className="text-right tabular-nums font-semibold">
                        {fmtIDR(filteredProducts.reduce((a, p) => a + (p.asset_value || 0), 0))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {fmtIDR(filteredProducts.reduce((a, p) => a + (p.hpp_value || 0), 0))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-600 font-semibold">
                        {fmtIDR(filteredProducts.reduce((a, p) => a + (p.potential_profit || 0), 0))}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
